import { Atom, Braces, Code2, Shuffle, Volume2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  setCurrentStep,
  setInterviewCompanyNamesIds,
  setSelectedInterviewCompany,
  setTestData
} from '../../redux/Slices/InterviewSlice';
import { Language } from '../../redux/Slices/InterviewSlice/types';
import {
  setPlansPopUpOpen,
  setpopUpPlanMessage
} from '../../redux/Slices/PaymentsSlice';
import { useAppDispatch, useAppSelector } from '../../redux/store/hooks';
import { useGenerateTestMutation } from '../../services/interViewApi';
import { useGetAllCompanyQuery } from '../../services/startTestAPI/startTestApi';
import Dropdown from '../Common/Dropdown';
import UpgradePlanPopUP from '../ReUsableComponents/UpgradePlanPopUP';

import ChooseDifficultyLevel from './ChooseDifficultyLevel';
type APIError = {
  status: number;
  data: {
    message: string;
    data: object;
    status: string;
  };
};
const languageIconMap: Record<string, JSX.Element> = {
  java: <Braces />,
  reactjs: <Atom />,
  python: <Code2 />,
  random: <Shuffle />,
  default: <Code2 />
};

const bgColorMap: Record<string, string> = {
  java: 'bg-orange-100 text-orange-600',
  reactjs: 'bg-blue-100 text-blue-600',
  python: 'bg-yellow-100 text-yellow-600',
  javascript: 'bg-yellow-100 text-yellow-600',
  c: 'bg-blue-100 text-blue-600',
  'c++': 'bg-purple-100 text-purple-600',
  random: 'bg-purple-100 text-purple-600',
  default: 'bg-gray-100 text-gray-600'
};

const ChooseLanguage = ({
  selectedLanguage,
  setSelectedLanguage
}: {
  selectedLanguage: Language | null;
  setSelectedLanguage: (language: Language) => void;
}) => {
  const { languages, difficultyLevel, testData } = useAppSelector(
    state => state.root.interview
  );
  const { isDriveTest, completedTestData } = useAppSelector(
    state => state.root.startFulltest
  );
  const [generateTest, { isLoading: genrateTestLoading }] =
    useGenerateTestMutation();
  const { data: allCompanies, error, isLoading } = useGetAllCompanyQuery({});
  const [selectedOption, setSelectedOption] = useState('');
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyId, setSelectedCompanyId] = useState('');
  const dispatch = useAppDispatch();
  const handleDropdownChange = (option: any) => {
    setSelectedOption(option.companyName);
    const companyID = option.id;
    setSelectedCompanyId(companyID);
    dispatch(setSelectedInterviewCompany(option));
  };
  useEffect(() => {
    if (allCompanies) {
      const companyData = allCompanies?.data || [];
      const resultArray = companyData
        ?.filter((each: any) => each?.isTestAvailable)
        ?.map((item: any) => ({
          companyName: `${item.companyName} ${item.examName}`.trim(),
          id: item.id
        }));
      dispatch(setInterviewCompanyNamesIds(resultArray));
      setCompanyOptions(resultArray);
    }
    if (error) {
      console.error('Error fetching companies:', error);
    }
  }, [allCompanies, error]);

  const handleClickContinue = async () => {
    if (isDriveTest) {
      setTestData(testData?.interviewTest);
    } else {
      try {
        const res = await generateTest({
          topicId: selectedLanguage?.id,
          companyId,
          questionDifficulty: difficultyLevel
        }).unwrap();
        dispatch(setCurrentStep('instructions'));
      } catch (error: APIError | any) {
        console.error(error, 'error678');
        if (error.status === 402) {
          const message = error?.data?.message;
          dispatch(setpopUpPlanMessage(message));
          dispatch(setPlansPopUpOpen(true));
        }
      }
    }
  };

  return (
    <div className='flex flex-col items-center justify-center bg-white p-4'>
      <div className='w-full max-w-4xl rounded-2xl bg-white'>
        <div className='mb-8 hidden text-center md:block'>
          <div className='mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-blue-500 to-purple-600'>
            <Volume2 className='h-8 w-8 text-white' />
          </div>
          <h1 className='mb-2 text-2xl font-bold text-gray-800'>
            Speaking Interview
          </h1>
        </div>
        <div className='mb-6 rounded-lg bg-white md:border md:border-gray-200 md:p-6'>
          <Dropdown
            getOption={(option: any) => `${option.companyName}`}
            options={companyOptions}
            label=''
            value={selectedOption}
            handleDropdownChange={option => handleDropdownChange(option)}
            width='w-fit'
            placeholder='Select Company'
            classes='cursor-pointer'
          />
        </div>
        <div className='mb-6 rounded-lg border border-gray-200 bg-white p-2 md:p-6'>
          <h2 className='mb-4 text-lg font-medium text-gray-700'>
            Select Programming Language
          </h2>
          <div className='mx-auto grid grid-cols-3 gap-4 md:grid-cols-5'>
            {languages.map(lang => {
              const icon =
                languageIconMap[lang.topic?.toLowerCase()] ||
                languageIconMap.default;
              const iconStyles =
                bgColorMap[lang?.topic?.toLowerCase()] || bgColorMap.default;

              return (
                <button
                  key={lang.id}
                  onClick={() => setSelectedLanguage(lang)}
                  className={`rounded-lg border-2 p-4 transition-all duration-200 ${
                    selectedLanguage?.id === lang.id
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div
                    className={`mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full ${iconStyles}`}
                  >
                    <span className='text-xl'>{icon}</span>
                  </div>
                  <div className='text-sm font-medium text-gray-700'>
                    {lang.topic}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <ChooseDifficultyLevel />
      <div className='mt-3 flex justify-center pb-4'>
        <button
          onClick={handleClickContinue}
          disabled={
            !selectedLanguage?.topic ||
            !companyId ||
            !difficultyLevel ||
            genrateTestLoading
          }
          className='rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-3 font-semibold text-white shadow-lg transition-all duration-200 hover:from-blue-600 hover:to-purple-700 disabled:cursor-not-allowed disabled:opacity-50'
        >
          Continue
        </button>
      </div>
      <UpgradePlanPopUP />
    </div>
  );
};

export default ChooseLanguage;
